import { passageUrl } from "./readerTools";
import { selectedPassage } from "./passageSelection";

import { remapMentions, mentionQuery, type SelectedMention } from "./reviewModel";
interface MentionUser { id: string | null; handle: string; displayName: string }
interface Composer { previous: string; selections: SelectedMention[] }
interface PreviewContext { title: string; text: string; url: string | null; currentUrl: string | null; revisionNumber: number | null; changed: boolean; available: boolean }
interface Backlink { id: string; title: string; url: string; state: string; anchored: boolean; kind: string }
export interface SavedDiscussion { threadId: string; messageId?: string }
interface ReviewOptions { onPageDiscussionSaved?: (form: HTMLFormElement, result: SavedDiscussion) => Promise<void> }

function element<K extends keyof HTMLElementTagNameMap>(tag: K, className?: string, text?: string): HTMLElementTagNameMap[K] {
  const value = document.createElement(tag);
  if (className) value.className = className;
  if (text !== undefined) value.textContent = text;
  return value;
}

async function request<T>(url: string, body?: unknown, key?: string): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json" };
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    headers.RequestVerificationToken = document.querySelector<HTMLMetaElement>('meta[name="request-verification-token"]')?.content ?? "";
    if (key) headers["Idempotency-Key"] = key;
  }
  const response = await fetch(url, { method: body === undefined ? "GET" : "POST", headers, body: body === undefined ? undefined : JSON.stringify(body), credentials: "same-origin", cache: "no-store" });
  if (!response.ok) {
    const error = await response.json().catch(() => ({})) as { detail?: string; title?: string; errors?: Record<string, string[]> };
    throw new Error(response.status === 401 ? "Your session expired. Sign in again before submitting." : error.detail ?? (error.errors ? Object.values(error.errors).flat().join(" ") : error.title) ?? "The request could not be completed.");
  }
  return await response.json() as T;
}

function badge(count: number) {
  document.querySelectorAll<HTMLElement>("[data-review-unread-count]").forEach(item => { item.textContent = String(count); item.hidden = count === 0; });
}

export function initializeReview(hooks: ReviewOptions = {}) {
  const composers = new WeakMap<HTMLTextAreaElement, Composer>();
  const seenMessages = new WeakSet<HTMLElement>();
  const visibleMessages = new Set<HTMLElement>();
  const pendingReads = new Set<string>();
  let savingReads = false;
  let readRetry: number | undefined;
  const observation = new IntersectionObserver(entries => {
    for (const entry of entries) {
      const message = entry.target as HTMLElement;
      if (entry.isIntersecting && entry.intersectionRect.width > 0 && entry.intersectionRect.height > 0) visibleMessages.add(message);
      else visibleMessages.delete(message);
    }
    acknowledgeVisible();
  });

  function acknowledgeVisible() {
    if (document.visibilityState !== "visible") return;
    for (const message of visibleMessages) {
      if (message.isConnected && message.dataset.reviewUnread === "true" && message.getClientRects().length) pendingReads.add(message.dataset.reviewMessage!);
    }
    void flushReads();
  }

  async function flushReads() {
    if (savingReads || pendingReads.size === 0) return;
    savingReads = true;
    const ids = [...pendingReads].slice(0, 100);
    try {
      const result = await request<{ count: number }>("/ui/review/read", { messageIds: ids, read: true });
      for (const id of ids) {
        pendingReads.delete(id);
        document.querySelectorAll<HTMLElement>(`[data-review-message="${CSS.escape(id)}"]`).forEach(item => { delete item.dataset.reviewUnread; });
        document.querySelectorAll(`[data-review-new="${CSS.escape(id)}"]`).forEach(item => item.remove());
      }
      badge(result.count);
    } catch {
      // A failed acknowledgement remains pending; viewing never silently loses a notification.
      window.clearTimeout(readRetry);
      readRetry = window.setTimeout(() => { if (document.visibilityState === "visible") void flushReads(); }, 5000);
      return;
    } finally { savingReads = false; }
    if (pendingReads.size) void flushReads();
  }

  const mentionMenu = element("div", "review-mention-menu");
  mentionMenu.id = "review-mention-options"; mentionMenu.setAttribute("role", "listbox"); mentionMenu.hidden = true;
  document.body.append(mentionMenu);
  let activeComposer: HTMLTextAreaElement | null = null;
  let options: MentionUser[] = [], selected = 0, queryVersion = 0;

  function closeMentions() {
    queryVersion++;
    mentionMenu.hidden = true;
    activeComposer?.setAttribute("aria-expanded", "false"); activeComposer?.removeAttribute("aria-activedescendant");
  }
  function spaceFor(node: Element) { return node.closest<HTMLElement>("[data-review-space-id]")?.dataset.reviewSpaceId ?? document.querySelector<HTMLElement>("[data-review-space-id]")?.dataset.reviewSpaceId; }
  async function suggest(area: HTMLTextAreaElement) {
    const query = mentionQuery(area.value, area.selectionStart), spaceId = spaceFor(area), version = ++queryVersion;
    if (!query || !spaceId) { closeMentions(); return; }
    activeComposer = area;
    try {
      const users = await request<MentionUser[]>(`/ui/review/mentions?spaceId=${encodeURIComponent(spaceId)}&query=${encodeURIComponent(query.query)}`);
      if (version !== queryVersion || activeComposer !== area || document.activeElement !== area) return;
      options = [...("agent".startsWith(query.query.toLowerCase()) ? [{ id: null, handle: "agent", displayName: "Request agent work" }] : []), ...users];
      selected = 0; drawMentions();
    } catch { closeMentions(); }
  }
  function drawMentions() {
    mentionMenu.replaceChildren();
    options.forEach((user, index) => {
      const item = element("button", "review-mention-option"); item.type = "button"; item.id = `review-mention-option-${index}`;
      item.setAttribute("role", "option"); item.setAttribute("aria-selected", String(index === selected));
      item.append(element("strong", undefined, "@" + user.handle), element("span", undefined, user.displayName));
      item.addEventListener("pointerdown", event => event.preventDefault()); item.addEventListener("click", () => chooseMention(index));
      mentionMenu.append(item);
    });
    if (!activeComposer || options.length === 0) { closeMentions(); return; }
    const bounds = activeComposer.getBoundingClientRect();
    mentionMenu.hidden = false; mentionMenu.style.width = `${Math.min(340, innerWidth - 24)}px`;
    positionPopup(mentionMenu, bounds);
    activeComposer.setAttribute("aria-expanded", "true"); activeComposer.setAttribute("aria-activedescendant", `review-mention-option-${selected}`);
    const selectedOption = mentionMenu.querySelector<HTMLElement>('[aria-selected="true"]');
    if (selectedOption) {
      const top = selectedOption.offsetTop, bottom = top + selectedOption.offsetHeight;
      if (top < mentionMenu.scrollTop) mentionMenu.scrollTop = top;
      else if (bottom > mentionMenu.scrollTop + mentionMenu.clientHeight) mentionMenu.scrollTop = bottom - mentionMenu.clientHeight;
    }
  }
  function chooseMention(index: number) {
    const area = activeComposer, user = options[index]; if (!area || !user) return;
    const query = mentionQuery(area.value, area.selectionStart); if (!query) return;
    const state = composers.get(area)!;
    const token = "@" + user.handle;
    const next = area.value.slice(0, query.start) + token + " " + area.value.slice(area.selectionEnd);
    state.selections = remapMentions(state.selections, area.value, next);
    if (user.id) state.selections.push({ start: query.start, length: token.length, userId: user.id });
    area.value = next; state.previous = next; area.focus(); area.setSelectionRange(query.start + token.length + 1, query.start + token.length + 1);
    area.dispatchEvent(new Event("input", { bubbles: true })); closeMentions();
  }

  const preview = element("div", "review-context-popover"); preview.hidden = true; preview.setAttribute("role", "dialog"); preview.setAttribute("aria-label", "Reference context");
  document.body.append(preview);
  let previewAnchor: HTMLElement | null = null, previewVersion = 0, previewTimer: number | undefined;
  async function openPreview(anchor: HTMLElement, focus = false) {
    const id = anchor.dataset.reviewReference ?? anchor.dataset.reviewAnchor; if (!id) return;
    previewAnchor = anchor; const version = ++previewVersion;
    preview.replaceChildren(element("p", undefined, "Loading context…")); preview.hidden = false; positionPopup(preview, anchor.getBoundingClientRect());
    try {
      const data = await request<PreviewContext>(`/ui/review/${anchor.dataset.reviewAnchor ? "anchors" : "references"}/${encodeURIComponent(id)}`);
      if (version !== previewVersion) return;
      const close = element("button", "text-button", "Close"); close.type = "button";
      close.addEventListener("click", () => { closePreview(); anchor.focus(); });
      const header = element("header"); header.append(element("strong", undefined, data.title), close);
      preview.replaceChildren(header);
      if (data.revisionNumber !== null) preview.append(element("small", undefined, `Revision ${data.revisionNumber}${data.changed ? " · Changed since referenced" : ""}`));
      preview.append(element("p", "review-context-text", data.text));
      const footer = element("footer");
      if (data.url) { const link = element("a", undefined, "Open referenced context"); link.href = data.url; footer.append(link); }
      if (data.changed && data.currentUrl) { const link = element("a", undefined, "Open current passage"); link.href = data.currentUrl; footer.append(link); }
      preview.append(footer); positionPopup(preview, anchor.getBoundingClientRect()); if (focus) close.focus();
    } catch (error) { if (version === previewVersion) preview.replaceChildren(element("p", undefined, error instanceof Error ? error.message : "Context unavailable.")); }
  }
  function closePreview() { preview.hidden = true; previewAnchor = null; previewVersion++; }
  function schedulePreviewClose() {
    window.clearTimeout(previewTimer);
    previewTimer = window.setTimeout(() => {
      if (!preview.matches(":hover") && !preview.contains(document.activeElement) && !previewAnchor?.matches(":hover, :focus")) closePreview();
    }, 180);
  }
  preview.addEventListener("pointerleave", schedulePreviewClose);

  const passageMenu = element("div", "review-passage-menu"); passageMenu.id = "passage-actions"; passageMenu.hidden = true; passageMenu.setAttribute("role", "dialog"); passageMenu.setAttribute("aria-label", "Paragraph and selection actions");
  let passageTrigger: HTMLElement | undefined;
  function closePassageMenu(returnFocus = false) {
    passageMenu.hidden = true;
    passageTrigger?.setAttribute("aria-expanded", "false");
    if (returnFocus) passageTrigger?.focus({ preventScroll: true });
  }
  document.body.append(passageMenu);
  async function copy(value: string, button: HTMLButtonElement, isLink = true) {
    const text = isLink ? new URL(value, location.href).href : value;
    try { await navigator.clipboard.writeText(text); const old = button.textContent; button.textContent = "Copied"; window.setTimeout(() => { button.textContent = old; }, 1400); }
    catch { window.prompt(isLink ? "Copy this link" : "Copy selected text", text); }
  }
  function pageIdentity() {
    const reader = document.querySelector<HTMLElement>("[data-qprins-static-document]");
    const footer = document.querySelector<HTMLElement>("[data-review-document-id][data-page-discussions]");
    return { documentId: footer?.dataset.reviewDocumentId, spaceId: footer?.dataset.reviewSpaceId,
      revisionId: reader?.dataset.revisionId ?? reader?.dataset.readingRevisionId };
  }
  function openPassageMenu(button: HTMLElement, nodeId: string) {
    if (!passageMenu.hidden && passageTrigger === button) { closePassageMenu(true); return; }
    const selection = selectedPassage(document, nodeId);
    closePassageMenu(); passageTrigger = button; button.setAttribute("aria-expanded", "true");
    passageMenu.replaceChildren(); passageMenu.hidden = false;
    const page = pageIdentity();
    const group = (label: string) => {
      const section = element("section", "review-passage-group"); section.setAttribute("aria-label", label);
      section.append(element("h3", "review-passage-group__title", label)); passageMenu.append(section); return section;
    };
    const paragraph = group("Paragraph");
    for (const [label, revision] of [["Copy paragraph link", undefined], ["Copy exact revision link", page.revisionId]] as const) {
      const item = element("button", "text-button", label); item.type = "button";
      item.addEventListener("click", () => { void copy(passageUrl(location.href, revision, nodeId), item); }); paragraph.append(item);
    }
    const canComment = document.querySelector("[data-selection-comment-form]") && page.documentId && page.spaceId;
    if (canComment) {
      const comment = element("button", "text-button", "Comment on paragraph"); comment.type = "button";
      comment.dataset.openPassageComment = nodeId; comment.setAttribute("aria-controls", "selection-comment-form");
      comment.addEventListener("click", () => { closePassageMenu(); }); paragraph.append(comment);
    }
    const editTemplate = document.querySelector<HTMLTemplateElement>("template[data-passage-editor-action]");
    const edit = editTemplate?.content.querySelector("button")?.cloneNode(true) as HTMLButtonElement | undefined;
    if (edit) { edit.dataset.editorNodeId = nodeId; edit.addEventListener("click", () => closePassageMenu()); paragraph.append(edit); }
    const backlinks = element("button", "text-button", "Discussion references"); backlinks.type = "button";
    backlinks.addEventListener("click", () => { if (page.documentId) void showBacklinks(page.documentId, nodeId); }); paragraph.append(backlinks);
    if (selection) {
      const selected = group("Selected text");
      selected.append(element("p", "review-passage-selection", `“${selection.text.length > 100 ? selection.text.slice(0, 97) + "…" : selection.text}”`));
      const copyText = element("button", "text-button", "Copy selected text"); copyText.type = "button";
      copyText.addEventListener("click", () => { void copy(selection.text, copyText, false); }); selected.append(copyText);
      if (canComment && selection.text.length <= 16_000) {
        const comment = element("button", "text-button", "Comment on selected text"); comment.type = "button";
        comment.dataset.openSelectionComment = nodeId; comment.dataset.selectionRevision = selection.revisionId;
        comment.dataset.selectionStart = String(selection.start); comment.dataset.selectionEnd = String(selection.end); comment.dataset.selectionText = selection.text;
        comment.setAttribute("aria-controls", "selection-comment-form"); comment.addEventListener("click", () => closePassageMenu()); selected.append(comment);
      }
    }
    positionPopup(passageMenu, button.getBoundingClientRect()); passageMenu.querySelector<HTMLButtonElement>("button")?.focus({ preventScroll: true });
  }
  async function showBacklinks(documentId: string, nodeId?: string) {
    closePassageMenu();
    const dialog = element("dialog", "review-backlinks-dialog");
    const header = element("header"); const close = element("button", "text-button", "Close"); close.type = "button";
    close.addEventListener("click", () => dialog.close()); header.append(element("h2", undefined, "Discussion references"), close);
    dialog.append(header, element("p", undefined, "Loading references…")); document.body.append(dialog); dialog.showModal();
    dialog.addEventListener("close", () => dialog.remove(), { once: true });
    try {
      const items = await request<Backlink[]>(`/ui/review/backlinks?documentId=${encodeURIComponent(documentId)}${nodeId ? "&nodeId=" + encodeURIComponent(nodeId) : ""}`);
      if (!dialog.isConnected) return;
      dialog.replaceChildren(header);
      const select = element("select"); select.setAttribute("aria-label", "Discussion state");
      for (const [value, label] of [["all", "All references"], ["open", "Open discussions"], ["resolved", "Resolved discussions"]]) { const option = element("option", undefined, label); option.value = value; select.append(option); }
      const list = element("div", "review-thread-list");
      const draw = () => {
        list.replaceChildren();
        const filtered = items.filter(item => select.value === "all" || item.state === select.value);
        if (!filtered.length) list.append(element("p", undefined, "No matching discussions reference this passage."));
        for (const item of filtered) {
          const row = element("article", "review-list-item"), link = element("a", undefined, item.title); link.href = item.url;
          row.append(link, element("small", undefined, `${item.anchored ? "Anchored here" : item.kind === "commit" ? "Commit reference" : "Referenced in a comment"} · ${item.state}`)); list.append(row);
        }
      };
      select.addEventListener("change", draw); dialog.append(select, list); draw();
    } catch (error) { dialog.append(element("p", "review-form-error", error instanceof Error ? error.message : "References unavailable.")); }
  }

  function scan() {
    document.querySelectorAll<HTMLElement>("[data-review-message]").forEach(message => {
      if (!seenMessages.has(message)) { seenMessages.add(message); observation.observe(message); }
    });
    document.querySelectorAll<HTMLTextAreaElement>("[data-review-composer]").forEach(area => {
      if (composers.has(area)) return;
      composers.set(area, { previous: area.value, selections: [] });
      area.setAttribute("aria-autocomplete", "list"); area.setAttribute("aria-controls", mentionMenu.id); area.setAttribute("aria-expanded", "false");
    });
    document.querySelectorAll<HTMLElement>("[data-qprins-static-document] [data-comment-anchor='block']").forEach(block => {
      if (block.querySelector(":scope > [data-review-passage-button]")) return;
      block.classList.add("review-addressable");
      const button = element("button", "review-passage-button"); button.type = "button"; button.dataset.reviewPassageButton = block.dataset.nodeId;
      button.dataset.readingIgnore = ""; button.setAttribute("aria-label", "Paragraph actions"); button.setAttribute("aria-haspopup", "dialog");
      button.setAttribute("aria-controls", passageMenu.id); button.setAttribute("aria-expanded", "false");
      block.append(button);
    });
    document.querySelectorAll<HTMLElement>("[data-review-reference], [data-review-anchor]").forEach(anchor => {
      if (anchor.dataset.reviewPreviewReady) return; anchor.dataset.reviewPreviewReady = "true";
      const button = element("button", "review-preview-button"); button.type = "button"; button.dataset.readingIgnore = "";
      button.setAttribute("aria-label", "Preview referenced context"); button.addEventListener("click", () => { void openPreview(anchor, true); });
      anchor.after(button);
    });
  }

  document.addEventListener("input", event => {
    const area = event.target; if (!(area instanceof HTMLTextAreaElement) || !composers.has(area)) return;
    const state = composers.get(area)!; state.selections = remapMentions(state.selections, state.previous, area.value); state.previous = area.value;
    void suggest(area);
  });
  document.addEventListener("keydown", event => {
    if (event.key === "Escape") {
      if (!mentionMenu.hidden || !preview.hidden || !passageMenu.hidden) event.preventDefault();
      closeMentions(); closePreview(); if (!passageMenu.hidden) closePassageMenu(true);
    }
    if (event.target !== activeComposer || mentionMenu.hidden) return;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") { event.preventDefault(); selected = (selected + (event.key === "ArrowDown" ? 1 : options.length - 1)) % options.length; drawMentions(); }
    if (event.key === "Enter" || event.key === "Tab") { event.preventDefault(); chooseMention(selected); }
  });
  document.addEventListener("pointerover", event => {
    const target = event.target instanceof Element ? event.target.closest<HTMLElement>("[data-review-reference], [data-review-anchor]") : null;
    if (target && target !== previewAnchor) { window.clearTimeout(previewTimer); previewTimer = window.setTimeout(() => { void openPreview(target); }, 120); }
  });
  document.addEventListener("pointerout", event => { if (event.target instanceof Element && event.target.closest("[data-review-reference], [data-review-anchor]")) schedulePreviewClose(); });
  document.addEventListener("focusin", event => {
    const target = event.target instanceof HTMLElement && event.target.matches("[data-review-reference], [data-review-anchor]") ? event.target : null;
    if (target) void openPreview(target);
  });
  document.addEventListener("focusout", schedulePreviewClose);
  document.addEventListener("pointerdown", event => {
    const target = event.target as Node;
    // Keep the native selection intact until the paragraph menu captures its exact text range.
    if (target instanceof Element && target.closest("[data-review-passage-button]")) event.preventDefault();
    if (!mentionMenu.contains(target) && target !== activeComposer) closeMentions();
    if (!passageMenu.contains(target) && !(target instanceof Element && target.closest("[data-review-passage-button]"))) closePassageMenu();
    if (!preview.contains(target) && !previewAnchor?.contains(target) && !(target instanceof Element && target.closest(".review-preview-button"))) closePreview();
  });
  document.addEventListener("click", event => {
    const target = event.target instanceof Element ? event.target : null;
    const copyButton = target?.closest<HTMLButtonElement>("[data-review-copy]"); if (copyButton) void copy(copyButton.dataset.reviewCopy!, copyButton);
    const passage = target?.closest<HTMLElement>("[data-review-passage-button]"); if (passage?.dataset.reviewPassageButton) { event.preventDefault(); openPassageMenu(passage, passage.dataset.reviewPassageButton); }
    const links = target?.closest<HTMLElement>("[data-review-backlinks]");
    if (links) void showBacklinks(links.dataset.reviewBacklinks!);
    const mark = target?.closest<HTMLButtonElement>("[data-review-read-id]");
    if (mark) { mark.disabled = true; void request<{ count: number }>("/ui/review/read", { messageIds: [mark.dataset.reviewReadId], read: mark.dataset.reviewReadValue === "true" }).then(() => location.reload()).catch(error => { mark.disabled = false; showNotice(error); }); }
    const follow = target?.closest<HTMLButtonElement>("[data-review-follow]");
    if (follow) { follow.disabled = true; void request(`/ui/review/threads/${follow.dataset.reviewFollow}/follow`, { muted: follow.dataset.reviewMuted === "true" }).then(() => location.reload()).catch(error => { follow.disabled = false; showNotice(error); }); }
  });

  document.addEventListener("submit", event => {
    const form = event.target; if (!(form instanceof HTMLFormElement) || (!form.hasAttribute("data-review-form") && !form.hasAttribute("data-review-endpoint"))) return;
    event.preventDefault(); void submit(form);
  });
  async function submit(form: HTMLFormElement) {
    if (form.dataset.reviewBusy) return;
    const error = form.querySelector<HTMLElement>(".review-form-error"); if (error) { error.hidden = true; error.textContent = ""; }
    const input = JSON.parse(form.dataset.reviewInput ?? "{}") as Record<string, unknown>;
    for (const control of form.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>("input[name],textarea[name],select[name]")) {
      if (control.name === "__RequestVerificationToken" || control.disabled) continue;
      let value: unknown = control.value;
      if (control instanceof HTMLInputElement && control.type === "checkbox") value = control.checked;
      else if (control.dataset.reviewType === "boolean") value = control.value === "true";
      else if (control instanceof HTMLInputElement && control.type === "number" || ["startPosition", "endPosition", "position"].includes(control.name)) value = control.value === "" ? null : Number(control.value);
      else if (["nodeId", "replyToMessageId"].includes(control.name) && control.value === "") value = null;
      setField(input, control.name, value);
    }
    const area = form.querySelector<HTMLTextAreaElement>("[data-review-composer]");
    if (area) {
      const leading = area.value.length - area.value.trimStart().length;
      const selections = (composers.get(area)?.selections ?? []).map(item => ({ ...item, start: item.start - leading })).filter(item => item.start >= 0);
      setField(input, area.name, area.value.trim());
      setField(input, area.name.includes(".") ? area.name.slice(0, area.name.lastIndexOf(".") + 1) + "mentions" : "mentions", selections);
    }
    const operation = form.dataset.reviewForm;
    let payload: unknown = input;
    if (operation) payload = { operation, targetId: form.dataset.reviewTarget ?? null,
      input: form.dataset.reviewDocumentId ? { documentId: form.dataset.reviewDocumentId, request: input } : input };
    const serialized = JSON.stringify(payload);
    if (form.dataset.reviewPayload !== serialized) { form.dataset.reviewKey = crypto.randomUUID(); form.dataset.reviewPayload = serialized; }
    form.dataset.reviewBusy = "true";
    const buttons = [...form.querySelectorAll<HTMLButtonElement>("button[type=submit]")]; buttons.forEach(button => button.disabled = true);
    try {
      const result = await request<{ url?: string; id?: string; threadId?: string; messageId?: string }>(form.dataset.reviewEndpoint ?? "/ui/review/commands", payload, form.dataset.reviewKey);
      if (form.hasAttribute("data-review-return-page") && result.threadId && hooks.onPageDiscussionSaved) {
        try { await hooks.onPageDiscussionSaved(form, { threadId: result.threadId, messageId: result.messageId }); }
        catch { throw new Error("Your comment was saved, but its display could not be updated. Retry to load it; the same submission will not create a duplicate."); }
        delete form.dataset.reviewPayload; delete form.dataset.reviewKey; delete form.dataset.reviewBusy;
        buttons.forEach(button => button.disabled = false);
        const composer = form.querySelector<HTMLTextAreaElement>("[data-review-composer]");
        if (composer) composers.set(composer, { previous: composer.value, selections: [] });
        void refreshBadge();
      } else if (result.url) location.assign(result.url);
      else if (operation === "commit_change_set" && result.id) location.assign(`/review/commits/${result.id}`);
      else if (operation === "discard_change_set") location.assign("/changes");
      else location.reload();
    } catch (cause) {
      const message = cause instanceof Error ? cause.message : "The change could not be saved.";
      if (error) { error.textContent = message; error.hidden = false; } else showNotice(cause);
      delete form.dataset.reviewBusy; buttons.forEach(button => button.disabled = false);
    }
  }

  document.addEventListener("visibilitychange", () => { if (document.visibilityState === "visible") { acknowledgeVisible(); void refreshBadge(); } });
  window.addEventListener("scroll", () => {
    if (previewAnchor && !preview.hidden) positionPopup(preview, previewAnchor.getBoundingClientRect());
    if (activeComposer && !mentionMenu.hidden) positionPopup(mentionMenu, activeComposer.getBoundingClientRect());
    closePassageMenu();
  }, { passive: true });
  window.addEventListener("resize", () => { closePreview(); closePassageMenu(); closeMentions(); });
  async function refreshBadge() { if (!document.querySelector("[data-review-unread-count]")) return; try { badge((await request<{ count: number }>("/ui/review/unread-count")).count); } catch { /* The next navigation or refresh retries. */ } }
  window.setInterval(() => { if (document.visibilityState === "visible") void refreshBadge(); }, 30000);
  let scanPending = false;
  new MutationObserver(() => { if (!scanPending) { scanPending = true; queueMicrotask(() => { scanPending = false; scan(); }); } }).observe(document.body, { subtree: true, childList: true });
  scan();
}

function positionPopup(popup: HTMLElement, anchor: DOMRect) {
  const width = Math.min(popup.offsetWidth || 360, innerWidth - 24), height = Math.min(popup.offsetHeight || 160, innerHeight - 24);
  popup.style.left = `${Math.max(12, Math.min(anchor.left, innerWidth - width - 12))}px`;
  popup.style.top = `${Math.max(12, Math.min(anchor.bottom + 8, innerHeight - height - 12))}px`;
}
function setField(target: Record<string, unknown>, path: string, value: unknown) {
  const parts = path.split("."); let current = target;
  for (const part of parts.slice(0, -1)) {
    if (["__proto__", "constructor", "prototype"].includes(part)) throw new Error("Invalid field name.");
    current = current[part] && typeof current[part] === "object" ? current[part] as Record<string, unknown> : (current[part] = {}) as Record<string, unknown>;
  }
  const key = parts.at(-1)!;
  if (["__proto__", "constructor", "prototype"].includes(key)) throw new Error("Invalid field name.");
  current[key] = value;
}
function showNotice(cause: unknown) {
  let notice = document.querySelector<HTMLElement>("[data-review-notice]");
  if (!notice) { notice = element("div", "review-notice"); notice.dataset.reviewNotice = ""; notice.setAttribute("role", "alert"); document.body.append(notice); }
  notice.textContent = cause instanceof Error ? cause.message : "The request could not be completed.";
  window.setTimeout(() => notice?.remove(), 8000);
}
